/**
 * The state shapes (web-plan.md F1) — the web twin of the desktop's
 * `state/` + `runtime/mod.rs` UiState, carrying exactly what F1–F3 screens
 * read. Slices are deliberately flat and JSON-ish: Immer shares unchanged
 * plain objects and arrays across snapshots, and every rule from the porting contract
 * (dedup by seq, unread as a formula, snapshot-not-delta) is written against
 * these shapes in `mutators.ts`.
 */
import type {
  Call,
  ChannelCard,
  ChannelSettings,
  ChatCommand,
  FriendsSnapshot,
  LinkPreview,
  Message,
  MessageRead,
  PetAbilities,
  PetCard,
  ReactionGroup,
  Restriction,
  Sticker,
  StickerPack,
  StickerPackSummary,
  UserStatus,
} from "../gen/dto";
import type { Preview } from "../lib/preview";

/** The optimistic bubble's sort key: above every real seq, below nothing.
 * (The Rust client uses u64::MAX; seqs here are JSON numbers and stay far
 * under 2^53, so MAX_SAFE_INTEGER is the same statement.) */
export const PENDING_SEQ = Number.MAX_SAFE_INTEGER;

export const WINDOW_SIZE = 300;
export const PAGE_SIZE = 50;
/** The typing cadence (chat-plan.md D464), the desktop's numbers: gateway
 * throttle 2 s < send period 2.5 s < expiry 4 s − tick 1 s, so every frame a
 * steady typist sends is accepted and the line goes 1.5–5 s after the last
 * keystroke. They were 5 / 5.5 / 8 / 2. */
export const TYPING_SEND_PERIOD_MS = 2_500;
export const TYPING_EXPIRY_MS = 4_000;
export const TICK_MS = 1_000;
export const DIRECTORY_TTL_MS = 30_000;
export const AFK_AFTER_MS = 300_000;
export const RING_FOR_MS = 30_000;
export const BOTTOM_SLACK = 48;
export const TOP_TRIGGER = 240;
/** How near the window's bottom a scroll asks for the page below it
 * (docs/fast-start.md D1091): `TOP_TRIGGER`, the other way. */
export const BOTTOM_TRIGGER = 240;
export const SETTLE_MS = 700;
/** How long the reading position has to hold still before what it shows counts
 * as read (D249): a pause of the eye, not a wheel notch on the way past — and
 * what keeps a flick through a hundred messages down to a handful of marks. */
export const SEEN_SETTLE_MS = 250;
/** How long a broadcast channel's feed has to hold still before what it shows
 * has been viewed (docs/broadcast-channels-plan.md D1081): long enough to have
 * looked, too long for a post scrolled past. */
export const VIEW_SETTLE_MS = 1000;
/** How often a post on screen is reported again (D1082): the report moves
 * nothing the second time, and its answer is the number's only way back to a
 * post nobody is scrolling. */
export const VIEW_REFRESH_MS = 60_000;
/** How far under the floating header the arrow down puts the first unread
 * message (dioxus-plan.md D408): room for the last read lines above it. The
 * desktop's `UNREAD_LANDING_PX`, where a channel opened on its backlog lands. */
export const UNREAD_LANDING_PX = 72;
/** How many of the newest readings of the gateway's clock the offset is taken
 * over (D298): enough for one of them to have crossed a quiet network, few
 * enough that a clock set right mid-session is believed within eight messages. */
export const CLOCK_SAMPLES = 8;
/** The most messages one gesture forwards (docs/forwarding.md D377) —
 * `echo_wire::dto::MAX_FORWARD`, Telegram's own ceiling for one forward. */
export const MAX_FORWARD = 100;
/** The most messages one selection holds, and so one deletion takes
 * (docs/message-selection.md D379, D380) — `echo_wire::dto::MAX_SELECTED`. */
export const MAX_SELECTED = 100;
/** How long after its send a message may still be edited (chat-plan.md D409):
 * an hour, on the gateway's clock — `echo_wire::dto::EDIT_WINDOW_MS`. Past it
 * the gateway refuses the edit, and this client offers none. */
export const EDIT_WINDOW_MS = 60 * 60 * 1000;
/** How long after its send its author's deletion of a message still takes it
 * from everybody (chat-plan.md D410): four hours, on the gateway's clock —
 * `echo_wire::dto::DELETE_WINDOW_MS`. Past it the gateway hides the message
 * from its author alone, and this client says «Delete for me». */
export const DELETE_WINDOW_MS = 4 * 60 * 60 * 1000;

export type SendState = "sent" | "preparing" | "sending" | "failed";

export interface Bubble {
  /** Rendering identity survives adoption of the server sequence number. */
  uiKey: string;
  message: Message;
  senderName: string;
  isOwn: boolean;
  /** Computed by regroup(): whether this bubble starts a visual group. */
  showHeader: boolean;
  state: SendState;
  sendProgress: number;
  /** Object URLs shown before the upload finishes (F2). */
  localPreviews: string[];
  /** A send of our own that waited off the feed while its files went up
   * (chat-plan.md D398) and joined it after: it does not take the feed to
   * its end — the press did (`FeedState.withheldSends`), and by now the
   * reader may have gone up to read. */
  withheld?: boolean;
  /** The header over a reply (D273), from the quote the reply itself carries
   * (`message.reply_to`): who wrote what it answers — resolveNames() — and
   * the start of what that says — fillReplyPreviews(), its spoilers still
   * hidden (D343). */
  replyAuthor: string | null;
  replyPreview: Preview | null;
  /** What it answers was deleted: the header stays, says so, and is nothing
   * to press. A reply with neither words nor this flag carries nothing
   * readable about its target and draws a header without words. */
  replyGone: boolean;
  /** Who wrote a forwarded message's words first (forwarding.md D376): the
   * name on its «Forwarded from» line — resolveNames(), from the header the
   * gateway wrote (`message.forwarded`). Null on a message its sender wrote,
   * and on a forward whose header this build could not read, which still
   * draws the line without a name. */
  forwardAuthor: string | null;
  /** Reactions live BESIDE the message (D147): an edit echo replaces the
   * message whole and the chips must survive it. */
  reactions: ReactionGroup[];
  myReaction: string | null;
  /** How many comments a broadcast channel's post has
   * (docs/broadcast-channels-plan.md D849) — beside the message for the
   * chips' reason, from a page's `comments` and the `comments` frame. 0 on
   * everything that is not a post. */
  comments: number;
  /** How many people have seen a broadcast channel's post (D1080): from a
   * page's `views` and the answer to a report of what is on screen. A count only
   * grows, so the larger number is the later one (D1082). 0 on everything that
   * is not a post. */
  views: number;
  /** The reader wrote it, as far as their copy says (`author`, D842): their
   * own words — or, for the staff, a message they sent in the channel's name,
   * which is still drawn on the channel's side and so is not `isOwn`. What
   * the edit and delete rules of a channel ask. */
  authored: boolean;
}

export type ComposerMode =
  | { t: "new" }
  | { t: "replying"; seq: number }
  | {
      t: "editing";
      seq: number;
    };

export interface FeedState {
  channelId: number; // 0 = empty pane
  channelName: string;
  bubbles: Bubble[];
  maxSeq: number;
  lastReadSeq: number;
  reachedStart: boolean;
  /** The window holds the channel's end: a page said so and nothing arrived
   * past it since (the desktop's `Feed::reached_end`, D271). Then a seq
   * between the newest bubble and `maxSeq` is a deleted message, never a
   * missed one — which is what lets the read mark reach the ceiling (D277). */
  reachedEnd: boolean;
  loadingOlder: boolean;
  /** A page below the window is on its way (docs/fast-start.md D1091, the
   * desktop's `Feed::loading_newer`): one a scroll asked for, a gap being closed,
   * or the window moving. */
  loadingNewer: boolean;
  /** Between open() and the first page: "no messages" and "not arrived yet"
   * must not draw alike. */
  loading: boolean;
  /** The window opened on a page read ahead of the opening (docs/fast-start.md
   * D1060) and its own pages are still on their way. `loading` stays true: it
   * gates the catch-up, the poll and the reopening, none of which may run under
   * an opening in the air. This says only that the pane has something to draw
   * meanwhile (`mutators.showsSpinner`) — the desktop's `FromCopy`. */
  drawnAhead: boolean;
  /** The seq the unread separator sits ABOVE, or null. */
  dividerBefore: number | null;
  mode: ComposerMode;
  /** Every new reply/edit and channel opening invalidates deferred composer cleanup. */
  composerRevision: number;
  /** Counts the sends pressed here whose bubble waits off the feed behind the
   * pill (D398): each takes the feed to its end at the press, where a send
   * shown at once takes it by appearing there. */
  withheldSends: number;
  /** The messages picked in this window (forwarding.md D377), ascending by
   * seq, as they were when picked. While anything is here the feed is
   * selecting: a click on a message picks it or puts it back, and the
   * composer's place is the bar that forwards them. Copies and not numbers,
   * so a pick survives the window being trimmed under it; a deletion still
   * takes its message out. openFeed() empties it. */
  selected: Message[];
  /** Why this window's opening failed, said over the composer until a page lands;
   * and what lets a second press on its row open it again (D1075). Null for a
   * window that opened, however empty. */
  error: string | null;
  /** When this window last sent a `typing` frame. Who is typing *to* us is
   * every chat's, not the feed's: `UiState.typing` (D464). */
  lastTypingSentMs: number;
  /** The broadcast channel whose name the messages here said `as_channel`
   * speak in (docs/broadcast-channels-plan.md D841): the channel itself in its
   * feed, and in a thread the channel of its post (D846, D848). Null in every
   * other chat. */
  voice: ChannelVoice | null;
  /** The message the window is to be brought to once it lands — a link to a
   * post (D859), the post a thread was opened from on the way back to it, or
   * the first unread past a window's end (D408). The feed lands on it once;
   * `openFeed` forgets it, and so does the window setting off elsewhere. */
  focusSeq: number | null;
}

/** A broadcast channel as the author of what is said in its name (D841): its
 * face, name and mark, drawn where a person's would be. */
export interface ChannelVoice {
  channelId: number;
  name: string;
  avatarFileId: string | null;
  verified: boolean;
}

export function emptyFeed(): FeedState {
  return {
    channelId: 0,
    channelName: "",
    bubbles: [],
    maxSeq: 0,
    lastReadSeq: 0,
    reachedStart: false,
    reachedEnd: false,
    loadingOlder: false,
    loadingNewer: false,
    loading: false,
    drawnAhead: false,
    dividerBefore: null,
    mode: { t: "new" },
    composerRevision: 0,
    withheldSends: 0,
    selected: [],
    error: null,
    lastTypingSentMs: 0,
    voice: null,
    focusSeq: null,
  };
}

/** Messages on their way to a chat (forwarding.md D377): picked in one channel
 * and waiting over the composer of another — or of the same one — until the
 * reader sends them or lets them go, as Telegram's forward bar waits. The
 * messages themselves and not their numbers: the channel they were picked in
 * closes when the one they go to opens, and the bar says what it is about to
 * send. Each send still names only where its message stands (`forward`). */
export interface ForwardDraft {
  /** The channel the messages are in: what each send names as its source. */
  from: number;
  /** The channel they go to: the one whose composer the bar stands over. */
  to: number;
  /** Ascending by seq — the order they were said in, and go out in. */
  messages: Message[];
  /** The broadcast channel they were picked in, as it stood then
   * (docs/broadcast-channels-plan.md D860): its posts, and its answers in a
   * post's comments, are in its name, which the bar says where a person's
   * would be. Kept here rather than read off its row when the bar is drawn —
   * by then a channel read without a subscription is forgotten (D837).
   * Absent outside a broadcast channel. */
  voice?: ChannelVoice;
}

/** Who wrote the message a row's line quotes, and what kind of words they are
 * (dioxus-plan.md D541) — the desktop's `rooms::Byline`. The id and not the
 * name: the name is the directory's, read when the row is drawn, so a rename
 * reaches the line without waiting for the next message. A forward that is
 * also a reply wears the forward. */
export interface Byline {
  senderId: number;
  mark: "forwarded" | "reply" | null;
}

export interface Room {
  channelId: number;
  ownerId: number;
  kind: string;
  peerId: number | null;
  name: string;
  description: string | null;
  avatarFileId: string | null;
  /** The line under the name: the newest message, its spoilers still hidden
   * (D343). */
  preview: Preview;
  /** The seq of the message `preview` quotes, 0 for none — what an edit or a
   * deletion is matched against. Not `maxSeq`: once the newest message is
   * deleted the line quotes an older one (D253). */
  previewSeq: number;
  /** Who wrote the message `preview` quotes, which the row names in front of
   * it (dioxus-plan.md D541); null for a line the server wrote and for no
   * line. Written with the line and cleared with it. */
  previewBy: Byline | null;
  /** When the quoted message goes (its `expires_at_ms`, the gateway's clock),
   * or null for one that stays: the line is a copy of its words, and has to
   * let go of them at the same deadline the chat does (D298). */
  previewExpiresAtMs: number | null;
  /** The line's question to the gateway (D468): which message is newest now,
   * after the one the line quoted went and nothing on this client could say.
   * `askLines` / `answerLine` read it; the writers of the line settle it. */
  line: LineAsk;
  timeText: string;
  unread: number;
  maxSeq: number;
  lastReadSeq: number;
  /** What the gateway said this row hashes to when it last served it
   * (docs/fast-start.md D1054), and what `known=` says back to be spared it; 0
   * for a row no answer vouched for — one made from a frame or a card. Frames do
   * not move it, on purpose: it says what the row was when the gateway served
   * it. */
  digest: number;
  /** How far anybody other than the reader has read here (chat-plan.md
   * D422): an own message at or below it wears two checks, above it one
   * (D423). From `seen_seq` on the served row and the `seen` frame, and it only
   * goes up. */
  seenSeq: number;
  /** How far the reader deleted this chat for themselves (friends-plan.md
   * D465): nothing at or below it is served to them again. The row itself
   * stays where it is, emptied — the chat is cleared, not gone. From
   * `cleared_seq` on the served row and the `history_cleared` frame; it only
   * goes up. */
  clearedSeq: number;
  /** Seqs past the read mark this client saw deleted (D277): the badge's
   * subtraction counts them, and refreshUnread() takes them back off. */
  holes: number[];
  lastMessageAtMs: number;
  callMembers: string[];
  callMemberIds: number[];
  member: boolean;
  admin: boolean;
  settings: ChannelSettings;
  members: number;
  joining: boolean;
  /** The reader stopped the pet this dialog is with (pets-plan.md D368): the
   * composer gives way to one Restart button. From `pet_stopped` on the served
   * row, kept current by the frame of that name; false on every other row and
   * from a gateway without pets. */
  petStopped: boolean;
  /** A public broadcast channel's handle (docs/broadcast-channels-plan.md
   * D870), null when it has none. */
  handle: string | null;
  /** Verified by the operator (D857): the mark after the name. */
  verified: boolean;
  /** The reader's staff rights in a broadcast channel (D844), as words —
   * one this build cannot name kept. Empty for everybody not on its staff. */
  rights: string[];
  /** What is taken from the reader in a broadcast channel, and until when
   * (D853); null when nothing is. A term that has run out reads as none. */
  restricted: Restriction | null;
  /** A public broadcast channel read without a subscription (D837, D859):
   * reached by a link, the search or a forward, and on no list — the state
   * lists a channel to its subscribers alone. A subscription makes it an
   * ordinary row at the next read of the state. */
  guest: boolean;
  /** When the newest security card waiting in this dialog came
   * (docs/official-pet-plan.md D943), on the gateway's clock; 0 for none —
   * every row but the official dialog's, and that one with no card waiting.
   * The row sorts by it as by a message and rises with the unread rows
   * (`mutators.resort`); the cards themselves are `UiState.securityNotices`,
   * and `mutators.raiseForNotices` writes this from them. */
  noticeAtMs: number;
}

/** Where a row's line stands with the gateway (D468). The line quotes the
 * newest message there is, and when that one goes somebody has to say which is
 * newest now: the open window can when it holds the channel's end; for every
 * other row the gateway's newest page is asked — the reader's own, without
 * what they hid (D407) or cleared (D465), with the service lines. */
export interface LineAsk {
  /** The line is blank because the message it quoted went, and nothing on
   * this client could say which message is newest now. */
  wanted: boolean;
  /** A question is out — one per row, however many deletions arrive while
   * it is. */
  asking: boolean;
  /** Moves, while the line waits, with everything in the chat an answer
   * already on its way may not have seen: a deletion, an edit, a deadline
   * armed, the chat cleared. An answer asked under an older one is asked
   * again. */
  epoch: number;
  /** The epoch the last question went out under, null for none since the
   * line went blank. Not asked again until the chat moves past it: a refused
   * question, or a page whose every message is past its deadline, waits for
   * the next deletion or the next sync instead of asking in a loop. */
  askedAt: number | null;
}

export function isDmRoom(room: Room): boolean {
  return room.kind === "dm";
}

/** A broadcast channel (docs/broadcast-channels-plan.md D836): only the
 * channel speaks in it, its people are subscribers, and it takes no calls. */
export function isBroadcastRoom(room: Room): boolean {
  return room.kind === "broadcast";
}

/** A former friend's conversation (friends-plan.md D411): a direct row nobody
 * is a member of any more — what the gateway keeps listing after an unfriend
 * when there was anything in it. Readable, never writable, on All alone, and
 * the one row that offers «Delete chat» (D412). */
export function isFormerDm(room: Room): boolean {
  return isDmRoom(room) && !room.member;
}

/**
 * A group (D274): a channel whose door is shut for good. It sits on the
 * Channels tab like any channel; only its settings say it cannot be opened.
 */
export function isGroupRoom(room: Room): boolean {
  return room.kind === "group";
}

/** The strip over the list (D179, pets-plan.md D361). `pets` is drawn only on
 * a gateway that knows pets (`AccountState.pets`); `broadcasts` only while the
 * account subscribes to a broadcast channel (docs/broadcast-channels-plan.md
 * D863, the Pets tab's rule). */
export type SidebarTab = "all" | "channels" | "broadcasts" | "friends" | "pets";

export interface Person {
  username: string;
  displayName: string | null;
  bio: string | null;
  avatarFileId: string | null;
  /** Unix seconds — the avatar colour seed's second half (D73). */
  createdAt: number;
  bubbleLight: string | null;
  bubbleDark: string | null;
  /** `pet` for a pet (pets-plan.md D349); absent — or a word this build does
   * not know — is a person, which is what a client older than pets draws
   * every account as (§10). */
  kind?: string;
  /** A pet's revision (D360): moves with every change to its card, its
   * commands above all. Absent for a person. */
  petRev?: number;
  /** The pet has been retired (D365): search skips it, its dialog shows a
   * notice where the composer was. */
  retired?: boolean;
  /** The stand's own pet (docs/official-pet-plan.md D934, D939): the verified
   * mark in place of the paw, a dialog nobody types into (D947), no Stop
   * (D937). Absent for everybody else. */
  official?: boolean;
}

/** Whether the account is a pet (D349) — everything that draws a paw, skips
 * a friendship or offers a pet's verbs asks this, and nothing else. */
export function isPet(person: Person | null | undefined): boolean {
  return person?.kind === "pet";
}

/** Whether the account is the stand's own pet (official-pet-plan.md D939):
 * a pet whose directory row says so, and nothing else makes one. */
export function isOfficial(person: Person | null | undefined): boolean {
  return isPet(person) && person?.official === true;
}

/** The people this client has met (docs/fast-start.md D1056): whoever an answer
 * named, merged and never replaced. Nothing here is everybody. A read that finds
 * nobody asks the gateway through `store/people.ts`. */
export interface DirectoryState {
  people: Record<number, Person>;
  selfId: number;
  selfIdFallback: number;
}

export interface FriendsState extends FriendsSnapshot {
  /** The first snapshot seeds silently — no toasts for a world that merely
   * already existed (D174). */
  seeded: boolean;
}

export interface PresenceState {
  /** Sparse: absent = offline (and an unknown status draws nothing). */
  map: Record<number, UserStatus>;
  /** The last AFK state we REPORTED — the hysteresis for activity frames. */
  selfAfk: boolean;
  /** Set solely by the presence key's presence in `GET /v1/bootstrap` — the
   * capability probe that gates ever sending an activity frame. */
  enabled: boolean;
}

export interface AccountState {
  displayName: string | null;
  bio: string | null;
  avatarFileId: string | null;
  createdAt: number;
  bubbleLight: string | null;
  bubbleDark: string | null;
  manualStatus: "dnd" | "invisible" | null;
  isAdmin: boolean;
  /** Pets on this server (pets-plan.md §10, D363) — `pets` of `GET /v1/me`,
   * the capability probe, and read off that answer alone and the same account
   * on `GET /v1/bootstrap`. `null` is a
   * gateway that does not know pets: the Pets tab and every pet control stay
   * hidden rather than offering what the server cannot do (the D274 rule).
   * `create` says whether this account may make one now. */
  pets: PetAbilities | null;
  /** «Send usage statistics», inverted (analytics-plan.md D437):
   * `analytics_off` of `GET /v1/me`, the same account on `GET /v1/bootstrap`, and
   * `PATCH /v1/me`, the only answers that carry it. `null` is a gateway that
   * keeps no product statistics, and
   * the profile hides the switch: there is nothing on the server to turn off.
   * The web sends no telemetry of its own, so it keeps no local copy. */
  analyticsOff: boolean | null;
}

export interface Ringing {
  channelId: number;
  /** Who started it, and their name as the directory has it now: a caller not met
   * yet is `#id` until the gateway answers (docs/fast-start.md D1057). */
  starterId: number;
  starterName: string;
  channelName: string;
  sinceMs: number;
}

export interface Toast {
  id: number;
  text: string;
  /** Clicking the toast opens this person's card (the request toast). */
  userId: number | null;
  /** …or this post's comments (a reply to the reader's comment,
   * docs/broadcast-channels-plan.md D861). */
  thread?: ThreadRef;
  /** …or this chat: a security notice's toast opens the official dialog,
   * where its card waits (docs/official-pet-plan.md D943). */
  channelId?: number;
}

/** Every `security_notice.kind` this build can word (official-pet-plan.md
 * D940) — `fixtures/official.json`'s `notice_kinds`; any other draws the
 * «update» line. */
export const NOTICE_KINDS = ["signin"] as const;

/** A security notice's card (docs/official-pet-plan.md D940–D943): the frame
 * as it came, and where the person is with it. Kept in the page alone and
 * never stored (D941): a reload, or a sign-out, and it is gone. */
export interface SecurityNotice {
  /** This page's own number for the card — the frame carries no id, and a
   * notice of a kind this build cannot name has no sign-in to go by. */
  id: number;
  /** `signin`, or a word a newer gateway says (D940): the card then says to
   * update, and offers nothing but to put it away. */
  kind: string;
  /** The new sign-in, as its line on the sessions list describes it. */
  signin: import("../gen/dto").NoticeSignin | null;
  /** When it happened, on the gateway's clock. */
  atMs: number;
  /** Waiting for an answer; its session being ended (D942); ended — the card
   * then says to change the password. */
  state: "waiting" | "ending" | "ended";
}

/** Which thread to open (D846): the channel, its post, and the thread's own
 * id. */
export interface ThreadRef {
  channelId: number;
  seq: number;
  threadId: number;
}

export interface VoicePeer {
  userId: number;
  senderId: number;
  username: string;
  muted: boolean;
  deafened: boolean;
  serverMuted: boolean;
  rttMs: number;
  screenSharing: boolean;
  screenSharePaused: boolean;
  screenShareHasAudio: boolean;
  /** Their camera is on (camera-plan.md D688): the browser draws only the
   * camera mark, never the picture (D703). Goes with the peer when they
   * leave. */
  cameraOn: boolean;
}

/** The one stream we are watching (F5) — null when none. */
export interface ShareView {
  userId: number;
  paused: boolean;
  hasAudio: boolean;
  /** Share audio's own volume, 0..2 (D99). */
  volume: number;
}

/** Our own live call (F4) — null when not in one. The read-only `calls`
 * map keeps describing every room's call; this slice is the one we are
 * inside, with the state only a participant has. */
export interface VoiceCall {
  channelId: number;
  /** "connecting" until media is up AND the MLS group is usable. */
  phase: "connecting" | "up";
  /** Somebody else was in the call with us while it was up — what makes it
   * a call worth asking about once it ends (docs/call-rating.md D515). Set
   * and never cleared for the life of the call. */
  company: boolean;
  muted: boolean;
  deafened: boolean;
  /** The R16 verification code, drawn nowhere (D1159): null until the keys
   * have arrived, which is what the call's state line reads it for. */
  code: string | null;
  /** Leaves no tile has accounted for past their allowance (D1149): while this
   * is not zero, nothing of ours is sealed or sent. */
  unlisted: number;
  peers: VoicePeer[];
  /** Who the SFU currently forwards (top-3), as user ids, self included. */
  speaking: number[];
  rttMs: number;
  lossPct: number;
  concealmentPct: number;
  /** Per-person receive volume, 0..2 (D33); absent = 1. */
  volumes: Record<number, number>;
  share: ShareView | null;
  /** Who each stream is being forwarded to, streamer → viewers, as user ids
   * with ourselves among them while we watch (screenshare-plan.md D336). The
   * relay's word; a stream with no entry is one it has not mentioned, which
   * is not the same as nobody watching. */
  audience: Record<number, number[]>;
}

/** A post's comments, open over the channel's feed
 * (docs/broadcast-channels-plan.md D846, D863): the feed is the thread's own,
 * and `selectedChannel` stays the channel — whose row holds the rights, the
 * settings and the subscription a comment is judged by. */
export interface ThreadView {
  /** The broadcast channel. */
  channelId: number;
  /** The post's seq in it. */
  seq: number;
  /** The thread's channel id — what the feed, the composer and every comment
   * route are opened on. */
  threadId: number;
  /** The post, drawn over the comments; null while it is read. */
  post: Message | null;
  /** How many people have seen it (D1080): what the channel's window held, then
   * what the report of the thread's opening answers. */
  views: number;
}

/** The two switches the publisher's next post goes out with (D851): comments
 * and reactions on (`true`) or off; absent is the channel's default (D852). */
export interface PostSwitches {
  comments?: boolean;
  reactions?: boolean;
}

export interface CardState {
  userId: number | null;
  /** null while the friendship answer flies — the card draws a placeholder,
   * never defaults (a card that briefly offers "Add" to a friend teaches
   * distrust). Never asked for a pet: a pet is nobody's friend (D364). */
  relation: import("../gen/dto").Friendship | null;
  /** A pet's card (pets-plan.md D362), fresh from `GET /v1/pets/{id}` each
   * time the card opens or the pet's revision moves; null while it flies and
   * for a person. */
  pet: PetCard | null;
}

/** A chat's `/` list (pets-plan.md D360), as this session last read it:
 * asked for when the list first opens in the chat, and dropped — to be asked
 * again — when a pet's `membership` frame arrives there or a pet's revision
 * moves past the one it was read at. `none` is a failed read, asked again
 * the next time the list opens. */
export type CommandsEntry =
  | { t: "loading" }
  | { t: "ready"; commands: ChatCommand[] }
  | { t: "none" };

/** Who read one of the reader's own messages, and when (chat-plan.md D422):
 * what «Details» in its menu draws (D423), by `readsKey`. Asked the first time
 * the submenu opens and kept for the next opening — `stale` once a `seen`
 * frame says a reader's mark has passed the message, or a resync says frames
 * may have been missed, and then asked again at that next opening. `failed`
 * is a read that did not come back, asked again the next time too. */
export type ReadsEntry =
  | { t: "loading"; stale: boolean }
  | { t: "ready"; reads: MessageRead[]; stale: boolean }
  | { t: "failed" };

/** How far this session got with a page's card (D280), by the URL as the
 * message spells it. `none` is for good when `retryAt` is null — the gateway
 * had nothing to show, or refused the address, or is older than the route —
 * and otherwise until then: over the fetch budget (429), a fetch that outlived
 * its request (503), a network or a server in trouble. A card draws only
 * `ready`. */
export type LinkPreviewEntry =
  | { t: "loading" }
  | { t: "ready"; preview: LinkPreview }
  | { t: "none"; retryAt: number | null };

/** The same for the card of a channel this reader holds no row of (D281),
 * keyed by `channelCardKey`: a 404 — unknown, not visible, a token that is
 * not the channel's live invite — is `none` for good. */
export type ChannelCardEntry =
  | { t: "loading" }
  | { t: "ready"; card: ChannelCard }
  | { t: "none"; retryAt: number | null };

/** One entry per channel *and* token: the same channel behind a live invite
 * and behind a spent one are two different answers. */
export function channelCardKey(channelId: number, token?: string | null): string {
  return token ? `${channelId}:${token}` : `${channelId}`;
}

/** The entry of a public broadcast channel's card asked by its handle
 * (docs/broadcast-channels-plan.md D871) — blind to case, as handles are. */
export function handleCardKey(handle: string): string {
  return `@${handle.toLowerCase()}`;
}

/** What the card under a link to a channel draws (D279), flat, so a card can
 * subscribe to it shallowly — from the reader's own row when they hold one,
 * and from `GET /v1/channels/{id}/card` (D281) when they do not. */
export interface ChannelLinkView {
  /** The row is the reader's: the channel can be opened from here. */
  held: boolean;
  kind: string;
  name: string;
  description: string | null;
  avatarFileId: string | null;
  /** The circle's second seed: a direct conversation wears its peer's face,
   * as its sidebar row does; every other channel is seeded by name alone. */
  faceCreatedAt: number;
  members: number;
  member: boolean;
  isPrivate: boolean;
  /** A join from the row's own bar or from a card is in flight. */
  joining: boolean;
  /** A public broadcast channel's handle (docs/broadcast-channels-plan.md
   * D870) and its operator's mark (D857), which its card shows. */
  handle: string | null;
  verified: boolean;
}

/** The gateway's clock against this machine's (disappearing-messages.md D298).
 * A deadline is written on the gateway's clock and let go of on this one, and
 * the two can disagree by more than a short lifetime. Each reading is the
 * gateway's clock as something left it, less `Date.now()` as it arrived — the
 * `sent_at_ms` of a message handed over the moment it was stored (a live
 * frame, the answer to a send), or `server_time_ms` of `GET /v1/bootstrap`, the
 * reading that comes before the first page — so it is the true offset less
 * the time spent on the way: never above it, and the largest of the last few
 * is the one that travelled fastest. */
export interface ClockState {
  /** The newest `CLOCK_SAMPLES` readings, oldest first. */
  samples: number[];
  /** Their maximum — gateway minus local, ms; 0 while there are none. */
  offsetMs: number;
}

/** What an attachment on its way out counts as on the pill over the composer
 * (chat-plan.md D398): the kind it travels as, so a picture through the
 * «File» door is a file. A voice note raises no pill and has no kind here. */
export type UploadKind = "photo" | "video" | "file";

/** The send whose attachments are going up (D398) — `lib/upload.ts` reads the
 * title, the numbers and the bar out of it. */
export interface UploadState {
  channelId: number;
  /** The send's first nonce — its bubble's. What the cross names, and what a
   * progress tick is matched on, so a late one cannot move the next send's
   * pill. */
  nonce: string;
  /** In the order they go. */
  items: UploadItem[];
}

/** One attachment of the send behind the pill (D398). */
export interface UploadItem {
  kind: UploadKind;
  /** The picked file's size — its share of the bar, known at the press. */
  weight: number;
  /** The bytes it goes up as — a photograph's two WebPs, a file as it is —
   * once they are known (`null` while it is encoding): what the numbers
   * count. */
  wire: number | null;
  /** How many of `wire` are up. */
  sent: number;
  /** 0..=1 of its encode and upload together. */
  progress: number;
}

/** The sticker sheet that is up (docs/stickers-plan.md D552, D566) — one at a
 * time, over the window: the catalogue, a pack, the reader's own packs
 * (`editor` with no id) or the editor of one. The desktop's `StickerSheet`. */
export type StickerSheet =
  | { t: "market" }
  | { t: "pack"; id: number }
  | { t: "editor"; id: number | null };

/** The catalogue as far as it has been read (D566) — only the question on
 * screen is answered: every new one takes a generation. */
export interface StickerCatalogState {
  query: string;
  order: "popular" | "new";
  packs: StickerPackSummary[];
  cursor: string | null;
  done: boolean;
  loading: boolean;
  failed: boolean;
  generation: number;
  asked: boolean;
}

/** Everything this client holds about stickers (D546–D566) — the desktop's
 * `state::stickers::Stickers`. The editor's staged file is its component's
 * own: a `Blob` has no business in a frozen store. */
export interface StickersState {
  /** Whether this gateway has stickers: a bare 404 on the installed list is
   * one older than them (chat-api.md §7), and the door stays shut. */
  support: "unknown" | "yes" | "no";
  /** The installed packs, in the account's order, each with its cover (D560). */
  installed: StickerPackSummary[];
  /** Newest first (D561). */
  recents: Sticker[];
  /** Packs read whole — the panel's sections, the sheets, the editor. */
  packs: Record<number, StickerPack>;
  /** Packs asked for and not answered: a section near the screen asks once. */
  loading: number[];
  /** Packs the gateway says this reader cannot see. */
  missing: number[];
  /** Packs whose last read failed for anything but a 404 — offline, a 5xx, a
   * 429: a sheet about one says «Could not load» and asks again only at a
   * press, instead of spinning for ever. Cleared by the next claim or answer. */
  failed: number[];
  /** How many writes each pack has had this session — an install or a
   * removal, an edit's answer, a deletion: what a read of it carries from its
   * claim, and lands only against. A read the gateway answered before a write
   * may arrive after it, and would undo it. */
  packWrites: Record<number, number>;
  /** …and the installed list's: every install, removal and pack forgotten. */
  installedWrites: number;
  /** The packs the reader made. */
  own: StickerPackSummary[];
  catalog: StickerCatalogState;
  sheet: StickerSheet | null;
  /** Where the sheet's arrow goes back to. */
  back: StickerSheet | null;
  /** The editor's request under way, and its last refusal. */
  editor: { busy: boolean; error: string | null };
  /** What each emoji alone in the box was last answered with (D574), by its
   * canonical form: the bar draws it at once the next time, while the
   * question is asked again. */
  suggestions: Record<string, Sticker[]>;
  /** Emoji asked about and not answered yet. */
  suggesting: string[];
}

export function emptyStickers(): StickersState {
  return {
    support: "unknown",
    installed: [],
    recents: [],
    packs: {},
    loading: [],
    missing: [],
    failed: [],
    packWrites: {},
    installedWrites: 0,
    own: [],
    catalog: {
      query: "",
      order: "popular",
      packs: [],
      cursor: null,
      done: false,
      loading: false,
      failed: false,
      generation: 0,
      asked: false,
    },
    sheet: null,
    back: null,
    editor: { busy: false, error: null },
    suggestions: {},
    suggesting: [],
  };
}

export interface UiState {
  connected: boolean;
  busy: boolean;
  /** THE one transient error line. */
  notice: string | null;
  /** Signed in, but the gateway opens nothing until this account proves an
   * e-mail address (D311, `GET /v1/me.must_confirm_email`). While it is set
   * the window shows the confirmation card instead of the app, and no socket
   * is opened — the gateway would refuse it. */
  mustConfirmEmail: boolean;
  selfUserId: number;
  selfUsername: string;
  directory: DirectoryState;
  rooms: Room[];
  selectedChannel: number | null;
  tab: SidebarTab;
  feed: FeedState;
  friends: FriendsState;
  presence: PresenceState;
  account: AccountState;
  calls: Record<number, Call>;
  voice: VoiceCall | null;
  ringing: Ringing | null;
  /** A call that ended for this person and waits for its rating
   * (docs/call-rating.md D515): the dialog over the window, a thumb down
   * and a thumb up. Closing it sends nothing. */
  callRating: { channelId: number } | null;
  card: CardState;
  toasts: Toast[];
  /** Link cards (D280, D281): what the gateway answered, for this session. */
  linkPreviews: Record<string, LinkPreviewEntry>;
  channelCards: Record<string, ChannelCardEntry>;
  /** The composer's clock (D298): channel id → the lifetime, in seconds, that
   * whatever is sent there next goes out under; absent — it stays. Here and
   * not in the composer, because a voice note and a dropped file are sent
   * from outside the box and go under the same clock. For the session and no
   * longer: never stored, gone with the rest of the state at sign-out, and
   * nothing the gateway hears of except as `ttl_s` on a send. */
  sendTtl: Record<number, number>;
  clock: ClockState;
  /** Channel id → its `/` list (pets-plan.md D360), for the session. */
  commands: Record<number, CommandsEntry>;
  /** `readsKey(channel, seq)` → who read that own message (D422, D423), for
   * the session. */
  reads: Record<string, ReadsEntry>;
  /** Who is typing in which chat (chat-plan.md D464): channel id → user id →
   * when their last frame came (ms). Every chat's, not only the open one's —
   * the header draws it in place of the subtitle and every list row in place
   * of the last message. A chat nobody is typing in has no entry. */
  typing: Record<number, Record<number, number>>;
  /** The forward waiting over a composer (forwarding.md D377) — one at a
   * time, as Telegram's bar: aiming another replaces it. Here and not on the
   * feed, which a channel switch resets: the forward is what opens the chat
   * it goes to. */
  forward: ForwardDraft | null;
  /** The send whose attachments are going up, while they are (chat-plan.md
   * D398) — what the pill over that chat's composer draws. At most one: the
   * send queue runs one send at a time. Here and not on the feed, which a
   * channel switch resets. */
  upload: UploadState | null;
  /** The words of a send called off from the pill (D398), or of an edit the
   * gateway did not take, on their way back to the box of `channelId`: its
   * composer takes them if it is empty and lets them go if something new is
   * typed there. */
  returnedDraft: { channelId: number; text: string } | null;
  /** Stickers (docs/stickers-plan.md): the installed packs, the recents, the
   * catalogue, the packs read whole and the sheet that is up. */
  stickers: StickersState;
  /** A post's comments open over its channel (docs/broadcast-channels-plan.md
   * D846), or null. */
  thread: ThreadView | null;
  /** Broadcast channel id → the switches of the post being written there
   * (D851): for that post alone, and gone with its send. */
  postSwitches: Record<number, PostSwitches>;
  /** Broadcast channel id → whether the comments written in its threads go
   * out in the channel's name (D848) — `comment_as_channel`'s holders' face
   * button, kept for the session. */
  commentAsChannel: Record<number, boolean>;
  /** The broadcast channel whose card is up (D864), or null. */
  broadcastCard: number | null;
  /** The security cards (docs/official-pet-plan.md D943), newest first: what
   * the official dialog draws at its foot. In the page alone (D941). */
  securityNotices: SecurityNotice[];
}

export function initialState(): UiState {
  return {
    connected: false,
    busy: false,
    notice: null,
    mustConfirmEmail: false,
    selfUserId: 0,
    selfUsername: "",
    directory: {
      people: {},
      selfId: 0,
      selfIdFallback: 0,
    },
    rooms: [],
    selectedChannel: null,
    tab: "all",
    feed: emptyFeed(),
    friends: {
      friends: [],
      incoming: [],
      outgoing: [],
      blocked: [],
      policy: "everyone",
      seeded: false,
    },
    presence: { map: {}, selfAfk: false, enabled: false },
    account: {
      displayName: null,
      bio: null,
      avatarFileId: null,
      createdAt: 0,
      bubbleLight: null,
      bubbleDark: null,
      manualStatus: null,
      isAdmin: false,
      pets: null,
      analyticsOff: null,
    },
    calls: {},
    voice: null,
    ringing: null,
    callRating: null,
    card: { userId: null, relation: null, pet: null },
    toasts: [],
    linkPreviews: {},
    channelCards: {},
    sendTtl: {},
    clock: { samples: [], offsetMs: 0 },
    commands: {},
    reads: {},
    typing: {},
    forward: null,
    upload: null,
    returnedDraft: null,
    stickers: emptyStickers(),
    thread: null,
    postSwitches: {},
    commentAsChannel: {},
    broadcastCard: null,
    securityNotices: [],
  };
}
